import axios from '@/plugins/axios';
import Vue from "vue";
import qs from 'qs';
import utils from '@/stores/utils';
import store from '@/stores/store';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const {
    getStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

/** Shared across all shipment tabs (section constraints applied at query time). */
const SHIPMENTS_FILTERS_STORAGE_KEY = getStorageKey('shipments');
const DEFAULT_USER_FILTERS = { limit: 20, ordering: '-delivery_date' };

const state = {
    section: null,
    shipments: [],
    /** User-chosen filters persisted across tabs (no section constraints). */
    shipments_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Effective filters for API (user + section overlay). */
    shipments_filters: { limit: 20, offset: 0, ordering: '-delivery_date' },
    shipments_user_filters_hydrated: false,
    shipments_count: 0,
    shipments_loaded: false,
    shipments_loading: false,
    shipments_loading_errors: null,
    shipments_cancel_source: null,

    shipments_stats: null,
    shipments_stats_loading: false,
    shipments_stats_loading_errors: null,
    shipments_stats_cancel_source: null,

    // Map shipments state
    map_shipments: [],
    map_shipments_loading: false,
    map_shipments_loading_errors: null,
    map_shipments_debounce_timer: null,

    // Geographic filters state
    geo_filters: null,

    // Selected items state
    selected_items: [],

    // Map expanded state
    map_expanded: false,

    // Bulk action loading state
    bulk_action_loading: false

};

/**
 * Hard tab constraints only — do not put soft defaults (e.g. exclude_readonly: false)
 * here or they would always win over user prefs.
 */
let getSectionFilters = function (section) {

    // Logisiticien :
    // - En attente - waiting


    if (section == 'waiting') {
        return {
            ready: true,
            readonly: false,
            status: 'waiting',
            delegated: true,
        };
    } else if (section == 'preparing') {
        return {
            ready: true,
            readonly: false,
            status: 'preparing',
        };
    } else if (section == 'prepared') {
        return {
            ready: true,
            readonly: false,
            status: 'prepared',
        };
    } else if (section == 'processing') {
        return {
            ready: true,
            readonly: false,
            status__in: 'preparing,prepared',
        };
    } else if (section == 'shipping') {
        return {
            ready: true,
            readonly: false,
            exclude_readonly: true,
            status: 'shipping',
        };
    } else if (section == 'all') {
        return {};
    } else if (section == 'drafts') {
        return {
            ready: false,
            readonly: false,
        };
    } else if (section == 'selfhandling_waiting') {
        return {
            ready: true,
            readonly: false,
            delegated: false,
            status: 'waiting',
        };
    } else if (section == 'selfhandling_preparing') {
        return {
            ready: true,
            readonly: false,
            delegated: false,
            status: 'preparing',
        };
    } else if (section == 'selfhandling_prepared') {
        return {
            ready: true,
            readonly: false,
            delegated: false,
            status: 'prepared',
        };
    } else if (section == 'selfhandling_shipping') {
        return {
            ready: true,
            readonly: false,
            delegated: false,
            status: 'shipping',
        };
    } else if (section == 'delegated') {
        return {
            ready: true,
            readonly: false,
            delegated: true,
        };
    } else if (section == 'delegated_waiting') {
        return {
            ready: true,
            readonly: false,
            delegated: true,
            status: 'waiting',
        };
    } else if (section == 'delegated_processing') {
        return {
            ready: true,
            readonly: false,
            delegated: true,
            status__in: 'waiting,preparing,prepared',
        };
    } else if (section == 'delegated_shipping') {
        return {
            ready: true,
            readonly: false,
            delegated: true,
            status: 'shipping',
        };
    } else if (section == 'providerorders') {
        return {
            ready: true,
            providerorders: true,
        };
    } else if (section == 'completed') {
        return {
            ready: true,
            readonly: true,
            exclude_readonly: false,
            // Override shared status chips so they cannot empty this tab.
            contextual_status: "complete,delivered,picked_up,canceled",
        };
    }

    return {};
};

/** Session counter key for a shipments tab — same keys as tab badges. */
const SECTION_STATS_KEYS = {
    waiting: 'waiting_shipments',
    preparing: 'preparing_shipments',
    prepared: 'prepared_shipments',
    shipping: 'shipping_shipments',
    all: 'shipments',
    drafts: 'draft_shipments',
    // No session counter yet for completed — always fetch the list.
    selfhandling_waiting: 'selfhandling_waiting_shipments',
    selfhandling_preparing: 'selfhandling_preparing_shipments',
    selfhandling_prepared: 'selfhandling_prepared_shipments',
    selfhandling_shipping: 'selfhandling_shipping_shipments',
    delegated: 'delegated_shipments',
    delegated_waiting: 'delegated_waiting_shipments',
    delegated_processing: 'delegated_processing_shipments',
    delegated_shipping: 'delegated_shipping_shipments',
    providerorders: 'providerorders_shipments',
};

const EMPTY_SHIPMENTS_STATS = {
    shipments: 0,
    items_count: 0,
    items_total: 0,
    total_purchase_price_excl_vat: 0,
};

const getSectionShipmentsCount = (stats, section) => {
    if (!stats) {
        return null;
    }
    const key = SECTION_STATS_KEYS[section];
    if (!key) {
        return null;
    }
    const count = stats[key];
    return count == null ? null : count;
};

const normalizeShipmentsFiltersForSearch = (section, filters) => {
    const search = String(filters.search || '').trim();
    if (!search) {
        return filters;
    }

    const normalized = {
        limit: filters.limit || 20,
        ordering: filters.ordering || '-delivery_date',
        ...(getSectionFilters(section) || {}),
        search,
    };
    if (filters.offset != null) {
        normalized.offset = filters.offset;
    }

    if (store.getters['session/current_entity_profile'] == 'customer') {
        if (filters.initiated != null) {
            normalized.initiated = filters.initiated;
        }
        if (filters.owner != null) {
            normalized.owner = filters.owner;
        }
    }

    return normalized;
};

const applyCustomerProfileShipmentFilters = (state, filters) => {
    if (store.getters['session/current_entity_profile'] != 'customer') {
        return filters;
    }
    return {
        ...filters,
        initiated: state.section != 'providerorders',
        owner: store.getters['session/current_entity_id'],
    };
};

function normalizeShipmentUserStatus(filters) {
    const normalized = { ...filters };
    if (normalized.status != null) {
        if (normalized.contextual_status == null) {
            const statuses = Array.isArray(normalized.status)
                ? normalized.status
                : String(normalized.status).split(',');
            normalized.contextual_status = [...new Set(statuses.flatMap(status =>
                status === 'waiting' ? ['to_be_processed', 'waiting'] : [status]
            ))].join(',');
        }
        delete normalized.status;
    }
    return normalized;
}

function loadSharedShipmentsUserFilters(section) {
    const shared = loadFiltersFromStorage(SHIPMENTS_FILTERS_STORAGE_KEY, null);
    const saved = shared || loadFiltersFromStorage(
        getStorageKey('shipments', section || 'all'),
        { ...DEFAULT_USER_FILTERS },
    );
    return normalizeShipmentUserStatus(extractUserFiltersFromEffective(
        saved,
        getSectionFilters(section || 'all'),
        {},
    ));
}

function persistShipmentsUserFilters(userFilters) {
    saveFiltersToStorage(SHIPMENTS_FILTERS_STORAGE_KEY, userFilters);
}

function rebuildShipmentsFilters(state, { resetOffset = false } = {}) {
    const section = state.section || 'all';
    const previousOffset = state.shipments_filters?.offset ?? 0;
    let filters = mergeUserAndSectionFilters(
        state.shipments_user_filters,
        getSectionFilters(section),
    );
    if (String(filters.search || '').trim()) {
        filters = normalizeShipmentsFiltersForSearch(section, filters);
    }
    state.shipments_filters = applyCustomerProfileShipmentFilters(state, filters);
    state.shipments_filters.offset = resetOffset ? 0 : previousOffset;
}

const mutations = {

    updateSection(state, section) {
        state.shipments = [];
        state.shipments_count = 0;
        state.shipments_loaded = false;
        state.shipments_loading = true;
        state.section = section;

        if (!state.shipments_user_filters_hydrated) {
            state.shipments_user_filters = {
                ...DEFAULT_USER_FILTERS,
                ...loadSharedShipmentsUserFilters(section),
            };
            state.shipments_user_filters_hydrated = true;
            persistShipmentsUserFilters(state.shipments_user_filters);
        }
        rebuildShipmentsFilters(state, { resetOffset: true });
    },

    updateShipments(state, shipments) {
        state.shipments = shipments;
        // Replace selected_items instances with newly fetched shipments based on id field
        state.selected_items = state.selected_items?.map((selected_item) => {
            return shipments.find((shipment) => shipment.id === selected_item.id) || selected_item;
        }) || [];
    },
    updateShipmentsCount(state, count) {
        state.shipments_count = count;
    },
    updateShipmentsLoading(state, loading) {
        state.shipments_loading = loading;
    },
    updateShipmentsLoaded(state, loaded) {
        state.shipments_loaded = loaded;
    },
    updateShipmentsLoadingErrors(state, errors) {
        state.shipments_loading_errors = errors;
    },
    updateShipmentsFilters(state, shipments_filters) {
        const section = state.section || 'all';
        const previousSearch = String(state.shipments_filters.search || '').trim();
        state.shipments_user_filters = normalizeShipmentUserStatus(extractUserFiltersFromEffective(
            shipments_filters,
            getSectionFilters(section),
            state.shipments_user_filters,
        ));
        state.shipments_user_filters_hydrated = true;
        persistShipmentsUserFilters(state.shipments_user_filters);

        let merged = mergeUserAndSectionFilters(
            state.shipments_user_filters,
            getSectionFilters(section),
        );
        const nextSearch = String(merged.search || '').trim();
        if (nextSearch && nextSearch !== previousSearch) {
            merged = normalizeShipmentsFiltersForSearch(section, merged);
        }
        state.shipments_filters = applyCustomerProfileShipmentFilters(state, merged);
        state.shipments_filters.offset = shipments_filters?.offset ?? 0;
    },

    updateShipmentsCancelSource(state, source) {
        state.shipments_cancel_source = source;
    },

    updateShipmentsStats(state, stats) {
        state.shipments_stats = stats;
    },

    updateShipmentsStatsLoading(state, loading) {
        state.shipments_stats_loading = loading;
    },

    updateShipmentsStatsLoadingErrors(state, errors) {
        state.shipments_stats_loading_errors = errors;
    },

    updateShipmentsStatsCancelSource(state, source) {
        state.shipments_stats_cancel_source = source;
    },

    updateMapShipments(state, map_shipments) {
        state.map_shipments = map_shipments;
    },

    updateMapShipmentsLoading(state, loading) {
        state.map_shipments_loading = loading;
    },

    updateMapShipmentsLoadingErrors(state, errors) {
        state.map_shipments_loading_errors = errors;
    },

    updateMapShipmentsDebounceTimer(state, timer) {
        state.map_shipments_debounce_timer = timer;
    },

    updateGeoFilters(state, geo_filters) {
        state.geo_filters = geo_filters;
    },

    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items || [];
    },

    updateMapExpanded(state, expanded) {
        state.map_expanded = expanded;
    },

    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },

};

const actions = {
    changeSection({ commit, dispatch, state }, section) {
        if (section == state.section) {
            return;
        }
        commit('updateSection', section);
        dispatch('fetchShipments');
        dispatch('session/fetchStats', null, { root: true });
    },

    exportShipmentItemssAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters.shipment_ready = state.shipments_filters.ready;
            filters.shipment_readonly = state.shipments_filters.shipment_readonly;
            filters.shipment_status = state.shipments_filters.status;
            filters.ordering = state.shipments_filters.ordering;
            filters.exclude_readonly_shipments = state.shipments_filters.exclude_readonly;
            filters.delivery_date_min = state.shipments_filters.delivery_date_min;
            filters.delivery_date_max = state.shipments_filters.delivery_date_max;
            filters.owner = state.shipments_filters.owner;
            filters.recipient = state.shipments_filters.recipient;
            filters.stock = state.shipments_filters.stock;
            filters.search = state.shipments_filters.search;
            filters.shipment_initiated = state.shipments_filters.initiated;
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = 5000;
        
        $utils.downloadFile({
            url: "/api/shipmentitems/",
            params: filters,
        }, $utils.getFilename('shipmentItems'));
    },

    exportShipmentsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.shipments_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/shipments/",
            params: filters,
        }, $utils.getFilename('shipments'));
    },

    async fetchShipments({ commit, dispatch, state, rootState }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("core.view_shipment") == -1) {
            return;
        }

        // Session tab counters already know this section is empty — skip list/stats/map requests.
        const sectionCount = getSectionShipmentsCount(
            rootState.session?.stats,
            state.section,
        );
        if (rootState.session?.stats_loaded && sectionCount === 0) {
            if (state.shipments_cancel_source) {
                state.shipments_cancel_source.cancel("canceled");
                commit('updateShipmentsCancelSource', null);
            }
            if (state.shipments_stats_cancel_source) {
                state.shipments_stats_cancel_source.cancel("canceled");
                commit('updateShipmentsStatsCancelSource', null);
            }
            if (state.map_shipments_debounce_timer) {
                clearTimeout(state.map_shipments_debounce_timer);
                commit('updateMapShipmentsDebounceTimer', null);
            }
            commit('updateShipments', []);
            commit('updateShipmentsCount', 0);
            commit('updateShipmentsLoading', false);
            commit('updateShipmentsLoaded', true);
            commit('updateShipmentsLoadingErrors', null);
            commit('updateShipmentsStats', { ...EMPTY_SHIPMENTS_STATS });
            commit('updateShipmentsStatsLoading', false);
            commit('updateMapShipments', []);
            commit('updateMapShipmentsLoading', false);
            return;
        }

        commit('updateShipmentsLoading', true);
        commit('updateShipmentsLoadingErrors', null);

        if (state.shipments_cancel_source) {
            state.shipments_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateShipmentsCancelSource', cancelSource);
        try {
            // Merge regular filters with geographic filters if they exist
            const filters = { ...state.shipments_filters };
            if (String(filters.search || '').trim()) {
                // Text search should behave like global search (NR-7).
                delete filters.geolocation;
            } else if (state.geo_filters) {
                const bounds = state.geo_filters;
                // New API format: geolocation parameter with 4 comma-separated values
                // Format: lat_min,lat_max,lon_min,lon_max
                filters.geolocation = `${bounds.southwest.lat},${bounds.northeast.lat},${bounds.southwest.lng},${bounds.northeast.lng}`;
            }
            if (!filters.ordering) {
                filters.ordering = '-delivery_date';
            }

            const response = await axios.get('/api/shipments/', {
                params: filters,
                cancelToken: state.shipments_cancel_source?.token
            });
            commit('updateShipmentsLoading', false);
            commit('updateShipments', response.data.results);
            commit('updateShipmentsCount', response.data.count);
            commit('updateShipmentsCancelSource', null);

            // Only trigger map shipments fetch if no geo filters are active
            // (to avoid double fetching when geo filters are already applied)
            if (!state.geo_filters) {
                dispatch('fetchShipmentsForMapDebounced');
            }

            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateShipmentsLoadingErrors', error.details);
            commit('updateShipmentsLoading', false);
            commit('updateShipmentsCancelSource', null);
            throw error;
        } finally {
            commit('updateShipmentsLoaded', true);
            dispatch('fetchShipmentsStats');
        }
    },

    async fetchShipmentsStats({ commit, state }) {

        commit('updateShipmentsStatsLoading', true);

        if (state.shipments_stats_cancel_source) {
            state.shipments_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateShipmentsStatsCancelSource', cancelSource);

        try {
            // Merge regular filters with geographic filters if they exist
            const filters = { ...state.shipments_filters };
            if (String(filters.search || '').trim()) {
                delete filters.geolocation;
            } else if (state.geo_filters) {
                const bounds = state.geo_filters;
                // New API format: geolocation parameter with 4 comma-separated values
                // Format: lat_min,lat_max,lon_min,lon_max
                filters.geolocation = `${bounds.southwest.lat},${bounds.northeast.lat},${bounds.southwest.lng},${bounds.northeast.lng}`;
            }

            const response = await axios.get('/api/shipments/stats/', {
                params: withSelectedIds(filters, state.selected_items),
                cancelToken: state.shipments_stats_cancel_source?.token
            });
            commit('updateShipmentsStats', response.data);
            commit('updateShipmentsStatsCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateShipmentsStatsLoading', false);
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateShipmentsStatsLoading', false);
        }
    },

    async assignShipment({ commit, dispatch, state }, params) {
        const url = `/api/shipments/${params.shipment.shipment_id}/assign/`;
        try {
            const response = await axios.put(url, { user: params.user });
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('fetchShipments');
            dispatch('session/fetchStats', null, { root: true });
        }
    },


    async setShipmentReadyness({ commit, dispatch, state }, params) {
        const url = `/api/shipments/${params.shipment.shipment_id}/ready/`;
        try {
            const response = await axios.put(url, { ready: params.ready });
            dispatch('fetchShipments');
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async setShipmentStatus({ commit, dispatch, state }, params) {

        try {
            const url = `/api/shipments/${params.shipment}/status/`;
            const payload = { status: params.status };
            if (params.delivered_date) {
                payload.delivered_date = params.delivered_date;
            }
            const response = await axios.put(url, payload)
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchShipmentsForMap({ commit, dispatch, state }, params) {
        commit('updateMapShipmentsLoading', true);
        commit('updateMapShipmentsLoadingErrors', null);

        try {
            const mapFilters = { ...state.shipments_filters };
            delete mapFilters.limit;
            delete mapFilters.offset;
            delete mapFilters.ordering;

            if (state.geo_filters) {
                const bounds = state.geo_filters;
                mapFilters.geolocation = `${bounds.southwest.lat},${bounds.northeast.lat},${bounds.southwest.lng},${bounds.northeast.lng}`;
            }

            const response = await axios.get('/api/shipments/map-points/', {
                params: mapFilters
            });

            const mapShipments = response.data.map(point => ({
                id: point.id,
                name: (point.recipient_name || '') + (point.deliverysite_name ? ' - ' + point.deliverysite_name : ''),
                type: 'shipment',
                address: point.address_full || '',
                position: {
                    lat: parseFloat(point.latitude),
                    lng: parseFloat(point.longitude)
                },
                rawData: {
                    type: 'shipment',
                    recipient: point.recipient_name,
                    deliverySite: point.deliverysite_name,
                    address: point.address_full,
                    status: point.status,
                    deliveryDate: point.delivery_date
                }
            }));
            commit('updateMapShipments', mapShipments);
            commit('updateMapShipmentsLoading', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateMapShipmentsLoading', false);
            commit('updateMapShipmentsLoadingErrors', error.details);
            throw error;
        }
    },

    fetchShipmentsForMapDebounced({ commit, dispatch, state }, params) {
        // Clear existing timer if any
        if (state.map_shipments_debounce_timer) {
            clearTimeout(state.map_shipments_debounce_timer);
        }

        // Set new timer
        const timer = setTimeout(() => {
            dispatch('fetchShipmentsForMap', params);
            commit('updateMapShipmentsDebounceTimer', null);
        }, 1000); // 1 seconds delay

        commit('updateMapShipmentsDebounceTimer', timer);
    },

    updateGeoFiltersAndRefresh({ commit, dispatch }, geo_filters) {
        commit('updateGeoFilters', geo_filters);
        // Only fetch shipments - this will automatically apply geo filters
        // and won't trigger the automatic map fetch since geo_filters is now set
        dispatch('fetchShipments');
    },

    clearGeoFiltersAndRefresh({ commit, dispatch }) {
        commit('updateGeoFilters', null);
        // Refresh shipments - this will clear geo filters and trigger map fetch automatically
        dispatch('fetchShipments');
    },

    updateSelectedItems({ commit }, selected_items) {
        commit('updateSelectedItems', selected_items);
    },

    updateMapExpanded({ commit }, expanded) {
        commit('updateMapExpanded', expanded);
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/shipments/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch("fetchShipments");
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },

};

const getters = {
    /**
     * Filters bound to Table UI: section constraints are hidden so tabs don't
     * surface forced values as user chips. API still uses shipments_filters.
     */
    shipments_table_filters(state) {
        const section = state.section || 'all';
        return omitFilterKeys(
            state.shipments_filters,
            getSectionConstraintKeys(getSectionFilters(section)),
        );
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
